import { TZDateMini } from '@date-fns/tz';
import { useCallback, useMemo } from 'react';
import { createIntl, useIntl, type FormatDateOptions } from 'react-intl';

import {
  getRegionalFormat,
  getTimezone,
} from '@dynatrace-sdk/user-preferences';

import { intlCache } from '../../../formatters/intl-cache.js';

type Options = {
  locale?: string;
  timeZone?: string;
  type?: 'datetime' | 'date' | 'time';
  precision?: 'minutes' | 'seconds' | 'milliseconds';
};

/**
 * Hook providing localized datetime format and placeholders for given locale and timezone. If no locale is specified, the user region  setting is used.
 * @internal
 */
export const useDateTimeMessages = (options?: Options) => {
  const {
    locale = getRegionalFormat(),
    timeZone: timeZoneProp = getTimezone(),
    type = 'datetime',
    precision = 'minutes',
  }: Options = options ?? {};

  const { formatMessage } = useIntl();
  const { formatDateToParts, timeZone } = useMemo(
    () => createIntl({ locale, timeZone: timeZoneProp }, intlCache),
    [locale, timeZoneProp],
  );

  const formatOptions = useMemo(() => {
    const options: FormatDateOptions = {};
    switch (type) {
      case 'date':
        options['year'] = 'numeric';
        options['month'] = '2-digit';
        options['day'] = '2-digit';
        return options; // Return config and ignore precision prop if only the date is shown.
      case 'datetime':
        options['year'] = 'numeric';
        options['month'] = '2-digit';
        options['day'] = '2-digit';
        options['hour'] = '2-digit';
        options['minute'] = '2-digit';
        break;
      case 'time':
        options['hour'] = 'numeric';
        options['minute'] = '2-digit';
        break;
    }

    switch (precision) {
      case 'seconds':
        options['second'] = '2-digit';
        break;
      case 'milliseconds':
        options['second'] = '2-digit';
        options['fractionalSecondDigits'] = 3;
        break;
    }

    return options;
  }, [type, precision]);

  const parts = useMemo(
    () =>
      formatDateToParts(TZDateMini.tz(getTimezone()).getTime(), formatOptions),
    [formatOptions, formatDateToParts],
  );

  const getPlaceholder = useCallback(
    (type: (typeof parts)[0]['type']) => {
      switch (type) {
        case 'year':
          return formatMessage({
            defaultMessage: 'YYYY',
            id: 'KgAwef2nXv2+/xgE',
            description: 'The abbreviation of the year part of a date value',
          });
        case 'month':
          return formatMessage({
            defaultMessage: 'MM',
            id: 'Bo8Z1UaJVL3ONFo8',
            description: 'The abbreviation of the month part of a date value',
          });
        case 'day':
          return formatMessage({
            defaultMessage: 'DD',
            id: '42g95tLLPXcqbFLl',
            description: 'The abbreviation of the day part of a date value',
          });
        case 'hour':
          return formatMessage({
            defaultMessage: 'hh',
            id: '5+yhrNhI2AlQmDAo',
            description: 'The abbreviation of the hour part of a time value',
          });
        case 'minute':
          return formatMessage({
            defaultMessage: 'mm',
            id: 'Xup0Ti6gaxC1KDSY',
            description: 'The abbreviation of the minute part of a time value',
          });
        case 'second':
          return formatMessage({
            defaultMessage: 'ss',
            id: 'ktoTdX660gW5R80Z',
            description: 'The abbreviation of the second part of a time value',
          });
        case 'fractionalSecond':
          return formatMessage({
            defaultMessage: 'fff',
            id: 'BBZfkJcLowIct62o',
            description:
              'The abbreviation of the fractional of the second part of a time value',
          });
        case 'dayPeriod':
          return formatMessage({
            defaultMessage: 'aa',
            id: 'B8hT0+wDrqrYuIxg',
            description:
              'The abbreviation of the day period (am/pm) part of a time value',
          });
        default:
          return type;
      }
    },
    [formatMessage],
  );

  const formatString = useMemo(
    () =>
      parts
        .map(({ type, value }) =>
          type === 'literal' ? value : getPlaceholder(type),
        )
        .join(''),
    [getPlaceholder, parts],
  );

  return {
    locale,
    timeZone,
    parts,
    formatOptions,
    formatString,
    getPlaceholder,
  };
};
